/**
 * Bloco padrão dos 11 estados de tela (F1.8; 03 §3.5 tabelas A e B; docs/03 §3).
 *
 * Por que este componente existe: a tabela A do contrato manda que cada um dos 11 estados tenha
 * **um texto e uma ação únicos** no produto inteiro. Se cada tela desenhasse o seu, "sem internet"
 * teria onze caras diferentes e alguma esqueceria de dizer que o trabalho está salvo. Aqui a
 * situação vira sempre o mesmo bloco: o que aconteceu, o que fazer agora e o que acontece com o que
 * a pessoa já fez.
 *
 * Regras que este arquivo carrega:
 *  - o texto vem de `ESTADOS`/`textoDoEstado` (`estados/tipos.ts`) — a tela só sobrescreve com
 *    justificativa escrita no código que chama;
 *  - **em nenhum estado dados locais são descartados** — por isso a linha "o que acontece com o seu
 *    trabalho" faz parte do bloco, não é enfeite;
 *  - toda "falha recuperável" oferece "Informar problema" (03 §3.5 regra iii; tela M20);
 *  - a mensagem é anunciada por leitor de tela (`aria-live`), porque quem está de luva no pátio
 *    muitas vezes não está olhando para a tela quando o estado muda.
 */
import type { CSSProperties } from 'react';
import type { DefinicaoEstado, EstadoTela } from '../estados/tipos.js';
import { textoDoEstado } from '../estados/tipos.js';
import { Botao } from './Botao.js';

export interface EstadoDaTelaProps {
  /** Um dos 11 estados. `null`/ausente = a tela está normal e nada é desenhado. */
  estado?: EstadoTela | null | undefined;
  /**
   * Texto/ação específicos desta tela (◐ da tabela B). `porque` é a justificativa exigida pelo
   * contrato: quem diverge do padrão precisa dizer por quê, no próprio código.
   */
  especifico?: { texto?: string; acao?: string | null; porque?: string } | undefined;
  /** O que fazer quando a pessoa toca a ação. Sem isto, o estado é só informação. */
  aoAgir?: (() => void) | undefined;
  /** Marque quando a ação do estado **é** a ação principal da tela (ex.: "Criar" no estado vazio). */
  acaoPrincipal?: boolean | undefined;
  /** Obrigatório nas telas que tratam "falha recuperável" (regra iii): leva à M20. */
  aoInformarProblema?: (() => void) | undefined;
  /** Força mostrar/esconder a linha do que acontece com o trabalho já feito. */
  mostrarDadosLocais?: boolean | undefined;
}

/** Peso visual por tom — só tokens, nenhum literal de cor. */
const CORES_POR_TOM: Record<
  DefinicaoEstado['tom'],
  { fundo: string; borda: string; texto: string }
> = {
  neutro: {
    fundo: 'var(--cor-superficie-2)',
    borda: 'var(--cor-linha)',
    texto: 'var(--cor-texto-suave)',
  },
  atencao: {
    fundo: 'var(--cor-alerta-suave)',
    borda: 'var(--cor-alerta)',
    texto: 'var(--cor-alerta)',
  },
  erro: {
    fundo: 'var(--cor-perigo-suave)',
    borda: 'var(--cor-perigo)',
    texto: 'var(--cor-perigo)',
  },
  sucesso: {
    fundo: 'var(--cor-sucesso-suave)',
    borda: 'var(--cor-sucesso)',
    texto: 'var(--cor-sucesso)',
  },
};

/**
 * Estados em que a linha "o que acontece com o seu trabalho" só faria ruído: ninguém precisa ser
 * lembrado de que nada se perdeu enquanto a tela ainda está abrindo ou está vazia.
 */
const SEM_LINHA_DE_DADOS: ReadonlySet<EstadoTela> = new Set<EstadoTela>([
  'carregando',
  'vazio',
  'acao_em_andamento',
]);

/** Estados em que a tela está ocupada — vira `aria-busy` para o leitor de tela. */
const OCUPADO: ReadonlySet<EstadoTela> = new Set<EstadoTela>(['carregando', 'acao_em_andamento']);

export function EstadoDaTela({
  estado,
  especifico,
  aoAgir,
  acaoPrincipal = false,
  aoInformarProblema,
  mostrarDadosLocais,
}: EstadoDaTelaProps) {
  // Tela normal: nada a dizer, nada a desenhar.
  if (!estado) return null;

  // O contrato exige justificativa escrita para quem diverge do texto padrão (03 §3.5, tabela B:
  // "◐ aplica com texto específico da nota"). Sem esse aviso, `porque` viraria enfeite: alguém
  // trocaria o texto no aperto e, seis meses depois, ninguém saberia por que aquela tela fala
  // diferente do resto do produto. O aviso só existe em desenvolvimento — não atrapalha o balcão.
  if (
    import.meta.env.DEV &&
    especifico &&
    (especifico.texto !== undefined || especifico.acao !== undefined) &&
    !especifico.porque
  ) {
    console.warn(
      `EstadoDaTela: a tela mudou o texto padrão do estado "${estado}" sem dizer por quê. ` +
        'Preencha "porque" com a justificativa (03 §3.5) ou use o texto padrão do produto.',
    );
  }

  const definicao = textoDoEstado(estado, especifico);
  const cores = CORES_POR_TOM[definicao.tom];
  const ocupado = OCUPADO.has(estado);
  const mostrarLinhaDeDados = mostrarDadosLocais ?? !SEM_LINHA_DE_DADOS.has(estado);

  const caixa: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--esp-2)',
    alignItems: 'flex-start',
    background: cores.fundo,
    border: '1px solid var(--cor-linha)',
    // Faixa lateral em vez de borda inteira: marca o tom sem gritar.
    borderLeftColor: cores.borda,
    borderLeftWidth: 'var(--foco-largura)',
    borderLeftStyle: 'solid',
    borderRadius: 'var(--raio-sm)',
    padding: 'var(--esp-4)',
  };

  return (
    <div
      data-estado={estado}
      // Erro precisa interromper a leitura; o resto é anunciado com educação.
      role={definicao.tom === 'erro' ? 'alert' : 'status'}
      aria-live={definicao.tom === 'erro' ? 'assertive' : 'polite'}
      aria-atomic="true"
      aria-busy={ocupado || undefined}
      style={caixa}
    >
      <p
        style={{
          margin: 0,
          color: cores.texto,
          fontSize: 'var(--font-size-formulario)',
          fontWeight: 'var(--peso-medio)',
          lineHeight: 'var(--altura-linha-normal)',
        }}
      >
        {definicao.texto}
      </p>

      {mostrarLinhaDeDados ? (
        <p
          style={{
            margin: 0,
            color: 'var(--cor-texto-suave)',
            fontSize: 'var(--font-size-formulario)',
            lineHeight: 'var(--altura-linha-normal)',
          }}
        >
          O que acontece com o seu trabalho: {definicao.dadosLocais}
        </p>
      ) : null}

      {definicao.acao !== null && aoAgir ? (
        <Botao variante={acaoPrincipal ? 'principal' : 'secundario'} onClick={aoAgir}>
          {definicao.acao}
        </Botao>
      ) : null}

      {/* Regra iii do 03 §3.5: falha recuperável sempre oferece o caminho de "Informar problema". */}
      {estado === 'falha_recuperavel' && aoInformarProblema ? (
        <Botao variante="texto" onClick={aoInformarProblema}>
          Informar problema
        </Botao>
      ) : null}
    </div>
  );
}
